// Display mirror of backend/src/lib/dataCategories.ts — kept identical by lib/__tests__/dpiaCriteria.test.ts.
// The server stores and reads the ids; the pickers offer them from here.
//
// Special category data is the GDPR Art. 9(1) list (UK GDPR alike, and the Data Protection (Jersey)
// Law 2018 Art. 1 once its criminal data limbs are taken out). Art. 9(1) names sex life and sexual
// orientation in one phrase; they are two entries here because they are two different facts about a
// person. Criminal offence data is not special category data: it is Art. 10 data with its own
// condition, so it stays a category of its own beside the nine.

export interface DataCategory { id: string; label: string }

export const SPECIAL_CATEGORY_DATA: readonly DataCategory[] = [
  { id: "racial_ethnic_origin",   label: "Racial or ethnic origin" },
  { id: "political_opinions",     label: "Political opinions" },
  { id: "religious_beliefs",      label: "Religious or philosophical beliefs" },
  { id: "trade_union_membership", label: "Trade union membership" },
  { id: "genetic",                label: "Genetic" },
  { id: "biometric",              label: "Biometric (to identify a person)" },
  { id: "health",                 label: "Health" },
  { id: "sex_life",               label: "Sex life" },
  { id: "sexual_orientation",     label: "Sexual orientation" },
];

/** The other categories offered beside the nine. */
export const GENERAL_DATA_CATEGORIES: readonly DataCategory[] = [
  { id: "personal",         label: "Personal" },
  { id: "financial",        label: "Financial" },
  { id: "criminal_offence", label: "Criminal offence" },
  { id: "location",         label: "Location" },
  { id: "behavioral",       label: "Behavioral" },
];

/**
 * Recorded before special category data was split into its nine kinds: special category data of a
 * kind nobody said. Still counts as special category data everywhere (it is never read as "none"),
 * is no longer offered, and asks for the kind to be recorded.
 */
export const UNSPECIFIED_SPECIAL_CATEGORY = "special_category";
export const UNSPECIFIED_SPECIAL_CATEGORY_LABEL = "Special category (kind not recorded)";

export const CRIMINAL_OFFENCE_DATA = "criminal_offence";

const SPECIAL_IDS: ReadonlySet<string> = new Set([...SPECIAL_CATEGORY_DATA.map((c) => c.id), UNSPECIFIED_SPECIAL_CATEGORY]);

/** Art. 9 data, including a record made before the split that does not say which kind. */
export const isSpecialCategory = (id: string): boolean => SPECIAL_IDS.has(id);

/** Art. 9 or Art. 10 data: what WP248 criterion 4 and Art. 35(3)(b) read. */
export const isSensitiveCategory = (id: string): boolean => isSpecialCategory(id) || id === CRIMINAL_OFFENCE_DATA;

export const hasUnspecifiedSpecialCategory = (ids: readonly string[]): boolean => ids.includes(UNSPECIFIED_SPECIAL_CATEGORY);

export function dataCategoryLabel(id: string): string {
  if (id === UNSPECIFIED_SPECIAL_CATEGORY) return UNSPECIFIED_SPECIAL_CATEGORY_LABEL;
  return [...SPECIAL_CATEGORY_DATA, ...GENERAL_DATA_CATEGORIES].find((c) => c.id === id)?.label ?? id.replace(/_/g, " ");
}
